import React, { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Package, AlertTriangle, Euro, RefreshCcw, ShoppingCart, Building2, Download, Plus, Pencil, Trash2, X, Save, History } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface MasterService {
    Id: number;
    ServiceName: string;
    SKU: string;
    Description: string;
    IsRenewable: boolean;
    IsPurchasableOnline: boolean;
    IsBillable: boolean;
    MinAuthorisedPrice: number | null;
    MaxAuthorisedPrice: number | null;
    ServiceDescription: string;
    CreatedAt: string;
    UpdatedAt: string;
    EntryCount: number;
    TotalSeconds: number;
    LastLoggedAt: string | null;
    HasAnyEntries: boolean;
}

interface AuditEntry {
    Id: number;
    Action: string;
    TargetType: string;
    TargetId: string;
    Details: string;
    PerformedBy: string;
    PerformedAt: string;
}

interface DepartmentStats {
    code: string;
    name: string;
    count: number;
}

const departmentNames: { [key: string]: string } = {
    'AC': 'Accounts',
    'CL': 'Client Services',
    'CO': 'Company Secretarial',
    'GEN': 'General',
    'TECH': 'Technology',
    'SAGE': 'Sage',
    'XERO': 'Xero',
    'ACL': 'Accounts Client'
};

// Read-only aggregates (EntryCount, TotalSeconds, LastLoggedAt, HasAnyEntries)
// are derived server-side and never sent back on create/update, so they're
// excluded from the form-shape here.
const emptyService: Omit<MasterService, 'Id' | 'CreatedAt' | 'UpdatedAt' | 'EntryCount' | 'TotalSeconds' | 'LastLoggedAt' | 'HasAnyEntries'> = {
    ServiceName: '',
    SKU: '',
    Description: '',
    IsRenewable: false,
    IsPurchasableOnline: false,
    IsBillable: false,
    MinAuthorisedPrice: null,
    MaxAuthorisedPrice: null,
    ServiceDescription: '',
};

const MasterServicesEditor: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [services, setServices] = useState<MasterService[]>([]);
    const [filteredServices, setFilteredServices] = useState<MasterService[]>([]);
    const [search, setSearch] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState<string>("all");
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState<MasterService | null>(null);
    const [formData, setFormData] = useState(emptyService);
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState<AuditEntry[]>([]);

    const getToken = useCallback(async () => {
        const token = await acquireTokenSafe(instance, accounts);
        return token;
    }, [instance, accounts]);

    const fetchServices = useCallback(async () => {
        if (accounts.length === 0) return;

        setLoading(true);
        try {
            const token = await getToken();
            if (!token) {
                setError('Your session has expired — please refresh the page to sign in again.');
                setLoading(false);
                return;
            }

            const response = await fetch(`${getApiBaseUrl()}/api/master-services`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('MasterServicesEditor: Response error:', errorText);
                throw new Error(`Failed to fetch services: ${response.status}`);
            }

            const data = await response.json();
            setServices(data);
            setFilteredServices(data);
        } catch (err: any) {
            console.error('MasterServicesEditor: Error:', err);
            setError(err.message || 'Failed to load services');
        } finally {
            setLoading(false);
        }
    }, [accounts, setLoading, getToken]);

    const fetchHistory = useCallback(async () => {
        try {
            const token = await getToken();
            if (!token) return;
            const res = await fetch(`${getApiBaseUrl()}/api/master-services/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setHistory(await res.json());
        } catch (err) {
            console.error('Error fetching history:', err);
        }
    }, [getToken]);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const getDepartment = (sku: string): string => {
        const match = sku.match(/^([A-Z]+)_/);
        return match ? match[1] : 'OTHER';
    };

    const departments: DepartmentStats[] = React.useMemo(() => {
        const deptCounts: { [key: string]: number } = {};
        services.forEach(s => {
            const dept = getDepartment(s.SKU);
            deptCounts[dept] = (deptCounts[dept] || 0) + 1;
        });
        return Object.entries(deptCounts)
            .map(([code, count]) => ({
                code,
                name: departmentNames[code] || code,
                count
            }))
            .sort((a, b) => b.count - a.count);
    }, [services]);

    useEffect(() => {
        let filtered = services;

        if (departmentFilter !== "all") {
            filtered = filtered.filter(s => getDepartment(s.SKU) === departmentFilter);
        }

        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (svc) =>
                    svc.ServiceName?.toLowerCase().includes(searchLower) ||
                    svc.SKU?.toLowerCase().includes(searchLower) ||
                    svc.Description?.toLowerCase().includes(searchLower) ||
                    svc.ServiceDescription?.toLowerCase().includes(searchLower)
            );
        }

        setFilteredServices(filtered);
    }, [search, departmentFilter, services]);

    const formatPrice = (price: number | null): string => {
        if (price === null || price === undefined) return '—';
        const num = typeof price === 'string' ? parseFloat(price) : price;
        if (isNaN(num)) return '—';
        if (num === 0) return '€0';
        return `€${num.toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    // CRUD operations
    const openCreateModal = () => {
        setEditingService(null);
        setFormData({ ...emptyService });
        setShowModal(true);
    };

    const openEditModal = (service: MasterService) => {
        setEditingService(service);
        setFormData({
            ServiceName: service.ServiceName,
            SKU: service.SKU,
            Description: service.Description,
            IsRenewable: service.IsRenewable,
            IsPurchasableOnline: service.IsPurchasableOnline,
            IsBillable: service.IsBillable,
            MinAuthorisedPrice: service.MinAuthorisedPrice,
            MaxAuthorisedPrice: service.MaxAuthorisedPrice,
            ServiceDescription: service.ServiceDescription,
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingService(null);
        setFormData({ ...emptyService });
    };

    const handleSave = async () => {
        if (!formData.ServiceName.trim() || !formData.SKU.trim()) {
            alert('Service Name and SKU are required.');
            return;
        }

        setSaving(true);
        try {
            const token = await getToken();
            if (!token) {
                alert('Your session has expired. Please refresh the page and sign in again.');
                return;
            }

            const url = editingService
                ? `${getApiBaseUrl()}/api/master-services/${editingService.Id}`
                : `${getApiBaseUrl()}/api/master-services`;

            const method = editingService ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errData.error || `Failed to ${editingService ? 'update' : 'create'} service`);
            }

            closeModal();
            await fetchServices();
            if (showHistory) fetchHistory();
        } catch (err: any) {
            alert(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            const token = await getToken();
            if (!token) {
                alert('Your session has expired. Please refresh the page and sign in again.');
                return;
            }

            const response = await fetch(`${getApiBaseUrl()}/api/master-services/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errData.error || 'Failed to delete service');
            }

            setDeleteConfirm(null);
            await fetchServices();
            if (showHistory) fetchHistory();
        } catch (err: any) {
            alert(err.message);
        }
    };

    const columns: TableColumn<MasterService>[] = [
        // Actions come FIRST so edit/delete are always visible — with 10+
        // columns the table scrolls horizontally on narrower screens, and a
        // trailing actions column silently disappears off the right edge
        // (this was reported as "the editor won't let anyone edit").
        {
            name: "Actions",
            cell: (row) => {
                // Deletion is blocked whenever ANY task_entries reference this
                // SKU (submitted, approved, or draft) — the backend enforces
                // this to avoid an FK violation. Disable the button up-front
                // and explain why in the tooltip, rather than surfacing a 409.
                const deleteBlocked = row.HasAnyEntries;
                const hrs = (row.TotalSeconds || 0) / 3600;
                const blockReason = deleteBlocked
                    ? (row.EntryCount > 0
                        ? `Cannot delete: ${row.EntryCount.toLocaleString('en-IE')} time entries (${hrs.toFixed(1)} hrs) logged. Archive or reassign first.`
                        : 'Cannot delete: draft time entries exist against this SKU. Archive or clear the drafts first.')
                    : 'Delete';
                return (
                    <div className="action-buttons">
                        <button className="action-btn edit-btn" onClick={() => openEditModal(row)} title="Edit">
                            <Pencil size={14} />
                        </button>
                        {deleteConfirm === row.Id ? (
                            <div className="delete-confirm-inline">
                                <button className="action-btn confirm-delete-btn" onClick={() => handleDelete(row.Id)} title="Confirm delete">
                                    <Trash2 size={14} />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setDeleteConfirm(null)} title="Cancel">
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                className="action-btn delete-btn"
                                onClick={() => setDeleteConfirm(row.Id)}
                                disabled={deleteBlocked}
                                title={blockReason}
                                style={deleteBlocked ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                );
            },
            width: "120px",
            ignoreRowClick: true,
        },
        {
            name: "Department",
            selector: (row) => getDepartment(row.SKU),
            sortable: true,
            cell: (row) => {
                const dept = getDepartment(row.SKU);
                return (
                    <span className={`dept-badge dept-${dept.toLowerCase()}`}>
                        {departmentNames[dept] || dept}
                    </span>
                );
            },
            width: "140px"
        },
        {
            name: "SKU",
            selector: (row) => row.SKU,
            sortable: true,
            cell: (row) => (
                <code className="sku-code">{row.SKU}</code>
            ),
            width: "160px"
        },
        {
            name: "Service Name",
            selector: (row) => row.ServiceName,
            sortable: true,
            cell: (row) => (
                <div className="service-cell">
                    <div className="service-name">{row.ServiceName}</div>
                    {row.Description && (
                        <div className="service-desc-short">{row.Description.substring(0, 60)}{row.Description.length > 60 ? '...' : ''}</div>
                    )}
                </div>
            ),
            grow: 2,
            minWidth: "250px"
        },
        {
            name: "Renewable",
            selector: (row) => row.IsRenewable ? 1 : 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.IsRenewable ? 'renewable' : 'one-off'}`}>
                    {row.IsRenewable ? (
                        <><RefreshCcw size={12} /> Yes</>
                    ) : 'No'}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Online",
            selector: (row) => row.IsPurchasableOnline ? 1 : 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.IsPurchasableOnline ? 'online' : 'offline'}`}>
                    {row.IsPurchasableOnline ? (
                        <><ShoppingCart size={12} /> Yes</>
                    ) : 'No'}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Billable",
            selector: (row) => row.IsBillable ? 1 : 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${row.IsBillable ? 'renewable' : 'one-off'}`}>
                    {row.IsBillable ? 'Yes' : 'No'}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Min Price",
            selector: (row) => row.MinAuthorisedPrice || 0,
            sortable: true,
            cell: (row) => (
                <span className="price-value">{formatPrice(row.MinAuthorisedPrice)}</span>
            ),
            right: true,
            width: "100px"
        },
        {
            name: "Max Price",
            selector: (row) => row.MaxAuthorisedPrice || 0,
            sortable: true,
            cell: (row) => (
                <span className="price-value">{formatPrice(row.MaxAuthorisedPrice)}</span>
            ),
            right: true,
            width: "100px"
        },
        {
            name: "Entries",
            selector: (row) => row.EntryCount || 0,
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${(row.EntryCount || 0) > 0 ? 'renewable' : 'one-off'}`}>
                    {(row.EntryCount || 0).toLocaleString('en-IE')}
                </span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Hours",
            selector: (row) => row.TotalSeconds || 0,
            sortable: true,
            cell: (row) => {
                const hrs = (row.TotalSeconds || 0) / 3600;
                return (
                    <span className="price-value">
                        {hrs > 0 ? hrs.toLocaleString('en-IE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—'}
                    </span>
                );
            },
            right: true,
            width: "100px"
        },
        {
            name: "Last Logged",
            selector: (row) => row.LastLoggedAt || '',
            sortable: true,
            cell: (row) => (
                <span className="cell-secondary">
                    {row.LastLoggedAt
                        ? new Date(row.LastLoggedAt).toLocaleDateString('en-IE', { year: 'numeric', month: 'short', day: 'numeric' })
                        : '—'}
                </span>
            ),
            width: "130px"
        },
    ];

    // Stats calculations
    const renewableCount = services.filter(s => s.IsRenewable).length;
    const onlineCount = services.filter(s => s.IsPurchasableOnline).length;
    const avgMinPrice = services.length > 0
        ? services.reduce((sum, s) => sum + (parseFloat(String(s.MinAuthorisedPrice)) || 0), 0) / services.length
        : 0;
    const avgMaxPrice = services.length > 0
        ? services.reduce((sum, s) => sum + (parseFloat(String(s.MaxAuthorisedPrice)) || 0), 0) / services.length
        : 0;

    // CSV Download function
    const downloadCSV = () => {
        const headers = [
            'Department',
            'SKU',
            'Service Name',
            'Description',
            'Is Renewable',
            'Is Purchasable Online',
            'Is Billable',
            'Min Authorised Price',
            'Max Authorised Price',
            'Service Description'
        ];

        const csvData = filteredServices.map(service => [
            getDepartment(service.SKU),
            service.SKU,
            service.ServiceName,
            service.Description || '',
            service.IsRenewable ? 'Yes' : 'No',
            service.IsPurchasableOnline ? 'Yes' : 'No',
            service.IsBillable ? 'Yes' : 'No',
            service.MinAuthorisedPrice ?? '',
            service.MaxAuthorisedPrice ?? '',
            service.ServiceDescription || ''
        ]);

        const escapeCSV = (value: any): string => {
            const str = String(value ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.map(escapeCSV).join(','),
            ...csvData.map(row => row.map(escapeCSV).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `master-services-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (error) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <Package size={28} />
                        <h1>Master Services Editor</h1>
                    </div>
                </div>
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading services: {error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Package size={20} />
                    <h1>Master Services Editor</h1>
                </div>
                <button className="download-btn" onClick={openCreateModal}>
                    <Plus size={16} />
                    Add Service
                </button>
            </div>

            <div className="stats-grid stats-grid-6">
                <div className="stat-card">
                    <div className="stat-icon">
                        <Package size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{services.length}</div>
                        <div className="stat-label">Total Services</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Building2 size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{departments.length}</div>
                        <div className="stat-label">Departments</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <RefreshCcw size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{renewableCount}</div>
                        <div className="stat-label">Renewable</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <ShoppingCart size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{onlineCount}</div>
                        <div className="stat-label">Online</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <Euro size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">€{avgMinPrice.toFixed(0)}</div>
                        <div className="stat-label">Avg Min Price</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <Euro size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">€{avgMaxPrice.toFixed(0)}</div>
                        <div className="stat-label">Avg Max Price</div>
                    </div>
                </div>
            </div>

            <div className="warning-banner compact">
                <AlertTriangle size={16} />
                <span>Pricing data — do not share without authorisation</span>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search services..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                />
                <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Departments ({services.length})</option>
                    {departments.map(dept => (
                        <option key={dept.code} value={dept.code}>
                            {dept.name} ({dept.count})
                        </option>
                    ))}
                </select>
                <button
                    onClick={downloadCSV}
                    className="download-btn"
                    title="Download as CSV"
                >
                    <Download size={16} />
                    Export CSV
                </button>
                <button
                    onClick={() => { setShowHistory(!showHistory); if (!showHistory && history.length === 0) fetchHistory(); }}
                    className="download-btn"
                    title="Change history"
                    style={{ background: showHistory ? 'var(--accent-blue)' : undefined, color: showHistory ? '#fff' : undefined }}
                >
                    <History size={16} />
                    History
                </button>
            </div>

            <div className="data-table-container master-services-table">
                <DataTable
                    columns={columns}
                    data={filteredServices}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    noDataComponent={
                        <div className="no-data">No services found</div>
                    }
                    customStyles={{
                        table: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '8px',
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: 'var(--bg-tertiary)',
                                borderBottomColor: 'var(--border-color)',
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                            },
                        },
                        headCells: {
                            style: {
                                color: 'var(--text-secondary)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        rows: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                            highlightOnHoverStyle: {
                                backgroundColor: 'var(--bg-hover)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        pagination: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                                borderTopColor: 'var(--border-color)',
                            },
                            pageButtonsStyle: {
                                color: 'var(--text-secondary)',
                                fill: 'var(--text-secondary)',
                                '&:hover:not(:disabled)': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                '&:disabled': {
                                    opacity: 0.4,
                                },
                            },
                        },
                        noData: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                            },
                        },
                    }}
                />
            </div>

            {/* Change History */}
            {showHistory && (
                <div className="card" style={{ marginTop: 20 }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <History size={18} /> Change History
                        </h3>
                        <button onClick={fetchHistory} className="download-btn" style={{ padding: '4px 12px', fontSize: 12 }}>
                            Refresh
                        </button>
                    </div>
                    <DataTable
                        columns={[
                            {
                                name: 'Time',
                                selector: (row: AuditEntry) => row.PerformedAt,
                                cell: (row: AuditEntry) => new Date(row.PerformedAt).toLocaleString(),
                                sortable: true,
                                width: '180px',
                            },
                            {
                                name: 'User',
                                selector: (row: AuditEntry) => row.PerformedBy,
                                sortable: true,
                                width: '220px',
                            },
                            {
                                name: 'Action',
                                width: '140px',
                                cell: (row: AuditEntry) => {
                                    const label = row.Action.replace(/_/g, ' ');
                                    const cls = row.Action.includes('create') ? 'renewable' : row.Action.includes('delete') ? 'one-off' : 'moderate';
                                    return <span className={`badge ${cls}`}>{label}</span>;
                                },
                            },
                            {
                                name: 'SKU',
                                selector: (row: AuditEntry) => row.TargetId,
                                width: '140px',
                            },
                            {
                                name: 'Details',
                                selector: (row: AuditEntry) => row.Details,
                                grow: 3,
                                wrap: true,
                            },
                        ]}
                        data={history}
                        pagination
                        paginationPerPage={10}
                        highlightOnHover
                        dense
                        noDataComponent={<div style={{ padding: 30, opacity: 0.5 }}>No changes recorded yet</div>}
                        customStyles={{
                            table: { style: { backgroundColor: 'var(--bg-secondary)' } },
                            headRow: { style: { backgroundColor: 'var(--bg-tertiary)', borderBottomColor: 'var(--border-color)', fontWeight: 600, fontSize: '0.85rem' } },
                            headCells: { style: { color: 'var(--text-secondary)' } },
                            rows: { style: { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', borderBottomColor: 'var(--border-color)' } },
                            cells: { style: { color: 'var(--text-primary)' } },
                            pagination: { style: { backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', borderTopColor: 'var(--border-color)' } },
                        }}
                    />
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingService ? 'Edit Service' : 'Add New Service'}</h2>
                            <button className="modal-close" onClick={closeModal}><X size={20} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Service Name *</label>
                                    <input
                                        type="text"
                                        value={formData.ServiceName}
                                        onChange={(e) => setFormData({ ...formData, ServiceName: e.target.value })}
                                        placeholder="Enter service name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>SKU *</label>
                                    <input
                                        type="text"
                                        value={formData.SKU}
                                        onChange={(e) => setFormData({ ...formData, SKU: e.target.value })}
                                        placeholder="e.g. AC_001"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <input
                                    type="text"
                                    value={formData.Description}
                                    onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                                    placeholder="Short description"
                                />
                            </div>
                            <div className="form-group">
                                <label>Service Description</label>
                                <textarea
                                    value={formData.ServiceDescription}
                                    onChange={(e) => setFormData({ ...formData, ServiceDescription: e.target.value })}
                                    placeholder="Detailed service description"
                                    rows={3}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Min Authorised Price (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.MinAuthorisedPrice ?? ''}
                                        onChange={(e) => setFormData({ ...formData, MinAuthorisedPrice: e.target.value ? parseFloat(e.target.value) : null })}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Max Authorised Price (€)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.MaxAuthorisedPrice ?? ''}
                                        onChange={(e) => setFormData({ ...formData, MaxAuthorisedPrice: e.target.value ? parseFloat(e.target.value) : null })}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.IsRenewable}
                                            onChange={(e) => setFormData({ ...formData, IsRenewable: e.target.checked })}
                                        />
                                        Is Renewable
                                    </label>
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.IsPurchasableOnline}
                                            onChange={(e) => setFormData({ ...formData, IsPurchasableOnline: e.target.checked })}
                                        />
                                        Is Purchasable Online
                                    </label>
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.IsBillable}
                                            onChange={(e) => setFormData({ ...formData, IsBillable: e.target.checked })}
                                        />
                                        Is Billable
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={closeModal}>Cancel</button>
                            <button className="btn-save" onClick={handleSave} disabled={saving}>
                                <Save size={16} />
                                {saving ? 'Saving...' : (editingService ? 'Update Service' : 'Create Service')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .master-services-table .sku-code {
                    font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
                    font-size: 0.8rem;
                    background: var(--bg-tertiary);
                    padding: 4px 8px;
                    border-radius: 4px;
                    color: var(--accent-color);
                }

                .master-services-table .service-cell {
                    padding: 8px 0;
                }

                .master-services-table .service-name {
                    font-weight: 500;
                    color: var(--text-primary);
                }

                .master-services-table .service-desc-short {
                    font-size: 0.75rem;
                    color: var(--text-muted);
                    margin-top: 2px;
                }

                .dept-badge.dept-ac { background: rgba(59, 130, 246, 0.15); color: #60a5fa; }
                .dept-badge.dept-cl { background: rgba(16, 185, 129, 0.15); color: #34d399; }
                .dept-badge.dept-co { background: rgba(168, 85, 247, 0.15); color: #c084fc; }
                .dept-badge.dept-gen { background: rgba(107, 114, 128, 0.15); color: #9ca3af; }
                .dept-badge.dept-tech { background: rgba(236, 72, 153, 0.15); color: #f472b6; }
                .dept-badge.dept-sage { background: rgba(34, 197, 94, 0.15); color: #4ade80; }
                .dept-badge.dept-xero { background: rgba(14, 165, 233, 0.15); color: #38bdf8; }
                .dept-badge.dept-acl { background: rgba(251, 146, 60, 0.15); color: #fb923c; }

                .master-services-table .price-value {
                    font-weight: 500;
                    color: var(--text-primary);
                }

                /* Action buttons */
                .action-buttons {
                    display: flex;
                    gap: 4px;
                    align-items: center;
                }

                .action-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 30px;
                    height: 30px;
                    padding: 0;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    line-height: 1;
                }

                .edit-btn {
                    background: rgba(59, 130, 246, 0.15);
                    color: #60a5fa;
                }
                .edit-btn:hover {
                    background: rgba(59, 130, 246, 0.3);
                }

                .delete-btn {
                    background: rgba(239, 68, 68, 0.15);
                    color: #f87171;
                }
                .delete-btn:hover {
                    background: rgba(239, 68, 68, 0.3);
                }

                .confirm-delete-btn {
                    background: rgba(239, 68, 68, 0.8);
                    color: white;
                }
                .confirm-delete-btn:hover {
                    background: rgba(239, 68, 68, 1);
                }

                .cancel-btn {
                    background: rgba(107, 114, 128, 0.15);
                    color: #9ca3af;
                }
                .cancel-btn:hover {
                    background: rgba(107, 114, 128, 0.3);
                }

                .delete-confirm-inline {
                    display: flex;
                    gap: 4px;
                }

                /* Modal styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    backdrop-filter: blur(4px);
                }

                .modal-content {
                    background: var(--bg-secondary);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    width: 90%;
                    max-width: 600px;
                    max-height: 80vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
                }

                .modal-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 20px 24px;
                    border-bottom: 1px solid var(--border-color);
                }

                .modal-header h2 {
                    margin: 0;
                    font-size: 1.1rem;
                    color: var(--text-primary);
                }

                .modal-close {
                    background: none;
                    border: none;
                    color: var(--text-secondary);
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                }
                .modal-close:hover {
                    background: var(--bg-hover);
                    color: var(--text-primary);
                }

                .modal-body {
                    padding: 24px;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .form-group label {
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: var(--text-secondary);
                }

                .form-group input[type="text"],
                .form-group input[type="number"],
                .form-group textarea {
                    background: var(--bg-tertiary);
                    border: 1px solid var(--border-color);
                    border-radius: 8px;
                    padding: 10px 12px;
                    color: var(--text-primary);
                    font-size: 0.9rem;
                    outline: none;
                    transition: border-color 0.15s ease;
                }

                .form-group input:focus,
                .form-group textarea:focus {
                    border-color: var(--accent-color);
                }

                .form-group textarea {
                    resize: vertical;
                    font-family: inherit;
                }

                .checkbox-group label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    flex-direction: row;
                }

                .checkbox-group input[type="checkbox"] {
                    width: 16px;
                    height: 16px;
                    accent-color: var(--accent-color);
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    padding: 16px 24px;
                    border-top: 1px solid var(--border-color);
                }

                .btn-cancel {
                    padding: 8px 16px;
                    border: 1px solid var(--border-color);
                    border-radius: 8px;
                    background: transparent;
                    color: var(--text-secondary);
                    cursor: pointer;
                    font-size: 0.85rem;
                }
                .btn-cancel:hover {
                    background: var(--bg-hover);
                }

                .btn-save {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 8px;
                    background: var(--accent-color);
                    color: white;
                    cursor: pointer;
                    font-size: 0.85rem;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-weight: 500;
                }
                .btn-save:hover {
                    opacity: 0.9;
                }
                .btn-save:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .page-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
            `}</style>
        </div>
    );
};

export default MasterServicesEditor;
